import Phaser from 'phaser';
import { SCENE, STREET_SPAWNS } from '../config.js';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { showToast } from '../ui/Toast.js';
import { messageFor } from '../api/errors.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { attachClockHand } from '../ui/clockHand.js';
import { reportLocation } from '../ui/locationTracker.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { shopRoom, shopGrid, buyState } from '../ui/shopInterior.js';

// The small pieces first, then the sofas and the beds (catalog `seats`), each cheapest first.
const OTHER_FURNITURE = ['lampa', 'zegar', 'obraz', 'szafa'];
function furnitureIds(furniture = {}) {
  const seat = (pose) => Object.keys(furniture).filter((id) => furniture[id].pose === pose)
    .sort((a, b) => furniture[a].price - furniture[b].price);
  return [...OTHER_FURNITURE, ...seat('sit'), ...seat('lie')];
}
const seatsLine = (n) => (n === 2 ? ' · 2 miejsca' : n === 1 ? ' · 1 miejsce' : '');
// Decoration ids are driven by the catalog so new items appear automatically.
const WHERE_PL = (allowed) => {
  const outdoor = ['farm', 'exterior'];
  const indoor = ['home', 'kitchen', 'salon', 'second_floor'];
  const isAll = indoor.concat(outdoor).every((s) => allowed.includes(s));
  if (isAll) return 'wszędzie';
  if (outdoor.every((s) => allowed.includes(s)) && !allowed.includes('home')) return 'na zewnątrz';
  if (indoor.every((s) => allowed.includes(s)) && !allowed.includes('farm')) return 'w domu';
  return '';
};

export class FurnitureShopScene extends Phaser.Scene {
  constructor() { super(SCENE.FURNITURE_SHOP); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create() {
    const exit = () => this.scene.start(SCENE.OUTSIDE, { spawnX: STREET_SPAWNS.FURNITURE });
    const { layer } = shopRoom(this, {
      bgKey: this.textures.exists('bg_furniture_shop') ? 'bg_furniture_shop' : 'bg_shop',
      title: '{sofa} Sklep meblowy', onExit: exit,
    });
    const tabs = [{ id: 'meble', label: '{sofa} Meble' }, { id: 'dekoracje', label: '{plant} Dekoracje' }];
    shopGrid(this, layer, { tabs, tabW: 180, tabStep: 188, cardsFor: (tab) => this._cards(tab) });

    new MoneyBar(this);
    reportLocation(this, null, null, { immediate: true });
    attachCoordsDebug(this);
  }

  // Meble: the small pieces, then every sofa and bed; Dekoracje: the catalog's decorations (new ones appear by themselves).
  _cards(tab) {
    const cat = gameState.raw?.catalog ?? {};
    const inv = gameState.raw?.inventory ?? {};
    if (tab === 'meble') {
      return furnitureIds(cat.furniture).filter((id) => cat.furniture?.[id]).map((id) => {
        const spec = cat.furniture[id];
        return {
          key: id, tex: `furniture_${id}`, name: spec.name_pl,
          sub: `masz: ${inv.unplaced_furniture?.[id] ?? 0}${spec.is_wardrobe ? ' {dress}' : ''}${seatsLine(spec.seats)}`,
          foot: `{coin} ${spec.price}`, state: buyState(spec.price),
          onTap: () => this._buy('furniture', id),
          // The wall clock's hand is a separate sprite: frozen at a friendly 2:00 here (a spinning hand would distract).
          decorate: (img) => attachClockHand(this, img, { type: 'furniture', item: id }, { fixedHours: 2 }) ?? [],
        };
      });
    }
    return Object.entries(cat.decorations ?? {}).map(([id, spec]) => {
      const where = WHERE_PL(spec.allowed_scenes ?? []);
      return {
        key: id, tex: `decoration_${id}`, name: spec.name_pl,
        sub: `masz: ${inv.unplaced_decorations?.[id] ?? 0}${where ? ` · ${where}` : ''}`,
        foot: `{coin} ${spec.price}`, state: buyState(spec.price),
        onTap: () => this._buy('decoration', id),
      };
    });
  }

  async _buy(kind, id) {
    try {
      if (kind === 'furniture') await gameState.buyFurniture(id);
      else await gameState.buyDecoration(id);
      const spec = (kind === 'furniture' ? gameState.raw.catalog.furniture : gameState.raw.catalog.decorations)[id];
      showToast(this, `Kupiono: ${spec.name_pl} {check}\nPostaw w świecie!`);
    } catch (e) { showToast(this, messageFor(e)); }
  }
}
